import { useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '@/lib/config';

/**
 * TurnstileWidget — §A10 Cloudflare Turnstile widget for the
 * anonymous branch of the Light-Gate overlay (2026-08-07 after
 * sister-session's b01a701 backend commit made `turnstileToken` a
 * required field on `POST /auth/register`).
 *
 * ═══ CONTRACT ══════════════════════════════════════════════════════
 *
 * The widget renders IN PLACE (inside the LightGateOverlay's register
 * form). It never navigates, never reloads the page — sixteen photos
 * sit in the parent's transient React state and a route change on
 * mobile drops them. That is the entire reason J-01 puts the gate at
 * the END of Schritt 1, and it is the reason this widget is loaded
 * via Cloudflare's script rather than any framework that owns page
 * lifecycle.
 *
 * ═══ WHY NOT AT THE ROUTE LEVEL ════════════════════════════════════
 *
 * Sebastian's ruling 2026-08-07: "The widget lives INSIDE the
 * Light-Gate overlay, not on a separate screen." A separate
 * verification screen before register would be a J-01 violation
 * (registration-up-front is the DreamStories anti-pattern §J-06
 * classifies) and would also drop the sixteen photos on the
 * navigation.
 *
 * ═══ RETURNING CUSTOMER ═══════════════════════════════════════════
 *
 * The Light-Gate's returning-customer branch never calls register (it
 * jumps straight to upload+create-order) and therefore never renders
 * this widget. That is enforced at LightGateOverlay's render-branch
 * split, not here.
 *
 * ═══ TOKEN LIFECYCLE ══════════════════════════════════════════════
 *
 * Cloudflare tokens are valid for ~5 minutes. On `expired-callback`
 * the widget auto-refreshes and calls back with a fresh token via
 * the standard `callback` hook. On `error-callback` (network fail,
 * script fail, etc.) we clear the token — the parent's submit gate
 * (`registerFormReady`) disables Weiter until a fresh token arrives.
 *
 * ═══ RENDER MODE ══════════════════════════════════════════════════
 *
 * Turnstile ships three visibility modes governed by the site key:
 *
 *   Managed (production default)     — invisible unless Cloudflare
 *                                       scores the visitor high risk
 *                                       and shows an interactive
 *                                       challenge.
 *   Non-Interactive                   — visible "Verifying..." tile
 *                                       that morphs into a checkmark.
 *   Invisible                         — no visible surface at all.
 *
 * Dev keys documented in `src/lib/config.ts`. The default dev key
 * `1x00000000000000000000AA` renders as a small visible tile that
 * auto-passes.
 *
 * ═══ EMPTY SITE KEY ═══════════════════════════════════════════════
 *
 * If `TURNSTILE_SITE_KEY` is empty (env var missing at build time),
 * the widget refuses to render and logs a console.error. Register
 * would 400 with no token, so failing loud here surfaces the config
 * error at widget-mount time instead of at register-submit time.
 */

interface TurnstileRenderOptions {
  readonly sitekey: string;
  readonly callback: (token: string) => void;
  readonly 'expired-callback': () => void;
  readonly 'error-callback': () => void;
}

interface TurnstileGlobal {
  readonly render: (container: HTMLElement, opts: TurnstileRenderOptions) => string;
  readonly remove: (widgetId: string) => void;
  readonly reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
    // Turnstile calls this global function when the script has loaded
    // and is ready to render widgets (we opt into explicit-render mode
    // via `?render=explicit` so widgets appear only where we place
    // them, not at every `.cf-turnstile` div).
    __turnstileOnLoad?: () => void;
  }
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__turnstileOnLoad';

export interface TurnstileWidgetProps {
  /** Called with the fresh token when Turnstile succeeds, with `null` on expiry or error. */
  readonly onToken: (token: string | null) => void;
}

export function TurnstileWidget({ onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Ref-stabilized callback so the effect deps stay empty and the
  // widget renders exactly once per mount. Re-rendering the widget
  // on every parent re-render would (a) waste API calls to
  // Cloudflare, (b) invalidate the current token, and (c) trip
  // Turnstile's "widget already rendered in this container" error.
  const onTokenRef = useRef(onToken);
  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      console.error(
        '[TurnstileWidget] VITE_TURNSTILE_SITE_KEY is empty — the widget will not render and register will 400. Set the env var per pwa/.env.example.',
      );
      return;
    }

    const renderWidget = (): void => {
      if (!containerRef.current) return;
      if (widgetIdRef.current !== null) return;
      if (!window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(null),
        'error-callback': () => onTokenRef.current(null),
      });
    };

    // Two entry points depending on script state:
    //   1. Script already loaded and window.turnstile ready → render now.
    //   2. Script not loaded → inject it and let its `onload` fire.
    // If the script is already in the DOM but window.turnstile isn't
    // ready yet (parallel mount before onload fires), we chain onto
    // the existing `__turnstileOnLoad` so both instances render.
    if (window.turnstile) {
      renderWidget();
    } else {
      const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
      const prevOnLoad = window.__turnstileOnLoad;
      window.__turnstileOnLoad = (): void => {
        if (prevOnLoad) prevOnLoad();
        renderWidget();
      };
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    return (): void => {
      // Clean up on unmount — remove the widget from Turnstile's
      // internal registry so a remount doesn't hit the "widget
      // already rendered" error.
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget was already removed or Turnstile lost its handle —
          // safe to swallow, cleanup is best-effort.
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
